export default {
  /**
   * Uses axios and the VusJS router to use CAS for login and logout :
   * - Login: redirects to the CAS login URL with the JWT server for the service.
   * - Logout: empties the local storage and redirect to the CAS logout URL.
   * - URL protected: checks if the URL has the meta "unistraCasAuthentication". If it's the meta use the login method.
   *
   * Documentation: https://git.unistra.fr/vue/cas-authentication
   *
   * @param {Object} vue - Vue instance.
   * @param {Object} router - VueJS Router instance; Init the router with the logout URL, cf. documentation.
   * @param {function} router.beforeEach - VueJS Router instance; Init the router with the logout URL, cf. documentation.
   * @param {Object} axios - Axios instance; Init this instance and add this to vue initialization.
   * @param {function} axios.post
   * @param options {Object} - Object containing the additional options.
   * @param options.appIsAllAuth {Boolean} - Option to indicate if the app is fully protected.
   * @param options.authCasLogoutUrl {String} - Name URL for logout. Init this route in the router.
   * @param options.jwtServerUrl {String} - URL of JWT server
   * @param options.serverCAS {String} - URL of CAS server
   */
  install (vue, { router, axios, options }) {
    if (!router) throw new Error('You must install Vue-Router and pass an instance as a parameter of the CAS call.')
    if (!axios) throw new Error('You must install Axios and Vue-Axios and pass an instance as a parameter of the CAS call.')

    const jwtServerUrl = ('jwtServerUrl' in options ? options.jwtServerUrl
                                                    : 'https://jwtserver-pprd.app.unistra.fr/api')
    const serverCAS = ('serverCAS' in options ? options.serverCAS : 'https://cas.unistra.fr')

    /**
     * Redirects the user to login URL on the CAS service.
     * For the service: JWT Server URI + "/redirect/" + Current page encoded in Base64
     */
    const login = () => {
      localStorage.clear()
      let service = `${jwtServerUrl}/redirect/${window.btoa(window.location.href)}`
      window.location = `${serverCAS}/cas/login?service=${encodeURIComponent(service)}`
    }

    /**
     * For logout, the plugin clears le local storage to delete the tokens.
     * Redirects the user to logout URL on the CAS service.
     */
    const logout = () => {
      localStorage.clear()
      window.location = `${serverCAS}/cas/logout`
    }

    /**
     * Checks if the token is expired.
     * Recovers the payload, decodes the payload with Base64 decode method.
     * Checks the exp field with the current timestamp.
     *
     * @param token {String}
     * @return {boolean}
     */
    const expired = token => {
      return JSON.parse(atob(token.split('.')[1])).exp < Math.trunc(Date.now() / 1000)
    }

    /**
     * If the acces token is expired, the plugin uses this method to refresh this token with the refresh token.
     * Do a axios call on the refresh URL to JWT Server with the refresh token in the data.
     *
     * @return {Promise<void>}
     */
    const refresh = async () => {
      await axios.post(`${jwtServerUrl}/token/refresh/`, { refresh: localStorage.getItem('JWT__refresh__token') })
                 .then(response => {
                   localStorage.setItem('JWT__access__token', response.data.access)
                 })
                 .catch(error => {
                   throw new Error('Vue cas authentication plugin error : ' + error.response.data)
                 })
    }

    router.beforeEach(
      async (to, from, next) => {
        if (to.name === options.authCasLogoutUrl) {
          /**
           * If the name of the current route is equal to that passed in the Options object on the parameters,
           * the plugin call logout method.
           */
          logout()
        } else if (!!to.query.service && !!to.query.ticket) {
          /**
           * If the URL has query string parameters after redirection from JWT Server, the plugin does a axios call
           * with the query string parameters in the data.
           * If the JWT Server sends the tokens, they are saved to local storage.
           * If the JWT Server sends a error, the plugin retries the login.
           */
          await axios.post(`${jwtServerUrl}/token/`, to.query)
                     .then(response => {
                       localStorage.setItem('JWT__access__token', response.data.access)
                       localStorage.setItem('JWT__refresh__token', response.data.refresh)
                       window.location = window.location.origin + window.location.pathname
                     })
                     .catch(() => {
                       login()
                     })
        } else if (
          options.appIsAllAuth ||
          to.matched.some(record => record.meta.unistraCasAuthentication)
        ) {
          /**
           * If the route has the meta "unistraCasAuthentication", the plugin checks if it's the tokens.
           * If not, the plugin call the login method.
           * If yes, the plugin lets the route pass.
           */
          if (!localStorage.getItem('JWT__refresh__token') || !localStorage.getItem('JWT__access__token')) {
            login()
          }
        }

        next()
      },
    )

    axios.interceptors.request.use(
      async config => {
        /**
         * If the URL contains "/token/" the plugin lets the request pass.
         * It's a security for axios call for login or refresh call.
         */
        if (config.url.match(/\/token\//)) {
          return config
        }

        /**
         * The case where the access token is not in storage not supported because, in the interceptor,
         * we don't know if the request secure or not.
         *
         * The conditions chain in the request interceptor is required to temporise the verification.
         */
        if (localStorage.getItem('JWT__access__token') !== null) {
          if (expired(localStorage.getItem('JWT__access__token'))) {
            if (localStorage.getItem('JWT__refresh__token') !== null) {
              if (expired(localStorage.getItem('JWT__refresh__token'))) {
                login()
              } else {
                await refresh()
              }
            } else {
              login()
            }
          }

          config.headers.Authorization = `Bearer ${localStorage.getItem('JWT__access__token')}`
        }

        return config
      },
      error => Promise.reject(error),
    )
  },
}
